// ============================================================================
// Approver Filter Options — GET /tort/approver/time-off-requests/filter-options
//
// Returns filter dropdown options for the approver incoming requests view.
//
// Required:
//  - x-nuid (header) → added automatically by the axios request interceptor
// ============================================================================

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'

// ---------------------------------------------------------------------------
// API response types
// ---------------------------------------------------------------------------

/** A single approver entry returned by the filter-options API. */
export interface ApproverEntry {
  name: string
  approvalGrpId: string
}

/**
 * Raw shape of the GET /tort/approver/time-off-requests/filter-options response.
 * All arrays contain pre-formatted display strings except `approvers`, which
 * uses structured objects so the label can be composed as "Name - ID".
 */
export interface ApproverFilterOptionsResponse {
  departments?: string[]
  unions?: string[]
  jobCodes?: string[]
  approvers?: ApproverEntry[]
}

// ---------------------------------------------------------------------------
// FilterComponent config shape (mirrors CheckboxFilterGroupConfig in FilterComponent.tsx)
// ---------------------------------------------------------------------------

/** Shape of a single checkbox filter group consumed by FilterComponent. */
export interface ApproverFilterGroup {
  title: string
  labels: string[]
  disabledLabels?: string[]
}

/** Full config map passed as `filtersConfig` prop to FilterComponent. */
export type ApproverFiltersConfig = Record<string, ApproverFilterGroup>

// ---------------------------------------------------------------------------
// Fixed fallbacks — status/type are fixed enums not returned by this API
// ---------------------------------------------------------------------------

const DEFAULT_STATUS_LABELS = [
  'All',
  'Submitted',
  'Approved',
  'Denied',
  'On Hold',
  'Withdrawn',
  'Retraction Submitted',
  'Retracted',
  'Resubmitted',
  'Returned',
]

const DEFAULT_REQUEST_TYPE_LABELS = [
  'All Request Types',
  'Vacation',
  'FLOAT',
  'FPD',
  'OTHER',
  'Vacation Bid',
  'Birthday',
]

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/**
 * Prepend "All" to a label list if it is not already present.
 * Returns an empty-safe result; an empty input yields `['All']`.
 */
const withAll = (labels: string[]): string[] => {
  if (labels.some((l) => /^all/i.test(l))) return labels
  return ['All', ...labels]
}

// ---------------------------------------------------------------------------
// Public mapper
// ---------------------------------------------------------------------------

/**
 * Maps the approver filter-options API response into the config shape
 * consumed by FilterComponent.
 *
 * - `departments`, `unions`, `jobCodes` are already formatted display strings.
 * - `approvers` objects are composed into "Name - approvalGrpId" labels.
 * - `statusFilter` and `requestTypeFilter` are fixed enums not returned by
 *   this API, so they always fall back to the built-in defaults.
 *
 * @param response - Raw API response, or null/undefined when not yet loaded.
 * @returns ApproverFiltersConfig ready to pass as `filtersConfig` to FilterComponent.
 */
export const mapApproverFilterOptionsResponse = (
  response: ApproverFilterOptionsResponse | null | undefined
): ApproverFiltersConfig => {
  // Compose "Name - approvalGrpId" labels from structured approver objects
  const approverLabels = (response?.approvers ?? [])
    .map(({ name, approvalGrpId }) => `${name} - ${approvalGrpId}`)
    .filter(Boolean)

  return {
    departmentFilter: {
      title: 'Department Code-Description',
      labels: withAll(response?.departments ?? []),
    },
    unionAffiliationFilter: {
      title: 'Union Affiliation',
      labels: withAll(response?.unions ?? []),
    },
    approvalGroupFilter: {
      title: 'Primary Approver/Approval Group',
      labels: withAll(approverLabels),
    },
    jobCodeFilter: {
      title: 'Job Code-Description',
      labels: withAll(response?.jobCodes ?? []),
    },
    // Status and request type are fixed business enums — not returned by this API
    statusFilter: {
      title: 'Request Status',
      labels: DEFAULT_STATUS_LABELS,
    },
    requestTypeFilter: {
      title: 'Request Type',
      labels: DEFAULT_REQUEST_TYPE_LABELS,
    },
  }
}

// ---------------------------------------------------------------------------
// Redux state
// ---------------------------------------------------------------------------

export interface ApproverFilterOptionsState {
  data: ApproverFilterOptionsResponse | null
  loading: boolean
  error: string | null
}

const initialState: ApproverFilterOptionsState = {
  data: null,
  loading: false,
  error: null,
}

// ---------------------------------------------------------------------------
// Thunk
// ---------------------------------------------------------------------------

/**
 * Fetches filter dropdown options for the approver incoming requests view.
 *
 * Dispatches `pending → fulfilled | rejected` lifecycle actions that are
 * handled by the slice's `extraReducers` to update `loading`, `data`, and
 * `error` state.
 *
 * The `x-nuid` header is attached automatically by the axios request
 * interceptor — no manual header work is required at the call site.
 */
export const getApproverFilterOptions = createAsyncThunk(
  'approverFilterOptions/getApproverFilterOptions',
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.approver.filterOptions
      const response = await api.get(url, { signal })
      return response.data as ApproverFilterOptionsResponse
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch approver filter options')
      )
    }
  }
)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const approverFilterOptionsSlice = createSlice({
  name: 'approverFilterOptions',
  initialState,
  reducers: {
    /**
     * Resets the approver filter options state to its initial values.
     * Useful when the approver view is unmounted or the user logs out.
     */
    clearApproverFilterOptions: (state) => {
      state.data = null
      state.error = null
      state.loading = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApproverFilterOptions.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getApproverFilterOptions.fulfilled, (state, action) => {
      state.loading = false
      state.data = action.payload
      state.error = null
    })
    builder.addCase(getApproverFilterOptions.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload as string
    })
  },
})

export const { clearApproverFilterOptions } = approverFilterOptionsSlice.actions
export default approverFilterOptionsSlice.reducer
